import { VueRenderer } from '@tiptap/vue-3';
import tippy from 'tippy.js';
import ItemSuggestionList from './ItemSuggestionList.vue';
import { getAllUi, getRestaurantHooksByProvider } from './ui';
import { filterInternalRookooArguments } from 'dashboard/helpers/toolHelper';

// Factory function that creates the suggestion object with store access
export const createItemSuggestion = store => {
  let dynamicAttributes = [];
  let availableTools = [];
  let availableLibraries = [];
  let availableVisualizations = [];

  // Fetch dynamic attributes from store
  const fetchAttributes = async () => {
    try {
      await store.dispatch('attributes/get');
      const allAttributes = store.getters['attributes/getAttributes'];

      // Transform store attributes to match our format
      dynamicAttributes = allAttributes.map(attr => ({
        id: attr.attribute_key,
        title: attr.attribute_display_name,
        label: attr.attribute_display_name, // Add label field for consistency
        description: `${attr.attribute_display_type} attribute`,
        category:
          attr.attribute_model === 'contact_attribute'
            ? 'Contact'
            : 'Conversation',
        icon: attr.attribute_model === 'contact_attribute' ? '👤' : '💬',
        itemType: 'attribute',
        attributeModel: attr.attribute_model,
        attributeType: attr.attribute_display_type,
      }));
    } catch (error) {
      // Handle error silently
      dynamicAttributes = [];
    }
  };

  // Helper function to get tool icon based on name
  const getToolIcon = toolName => {
    const iconMap = {
      QueryBuilder: '🔍',
      Lookup: '❓',
      Recommendation: '💡',
      Intake: '📋',
      Escalate: '⬆️',
    };
    return iconMap[toolName] || '🔧';
  };

  // Fetch tools from store with improved logic to prevent duplicate calls
  const fetchTools = async () => {
    try {
      // Check if tools are fresh to avoid unnecessary API calls
      const isToolsFresh = store.getters['tools/isAvailableToolsFresh'];
      const isFetchingTools =
        store.getters['tools/getUIFlags'].isFetchingAvailable;

      if (!isToolsFresh && !isFetchingTools) {
        await store.dispatch('tools/getAvailable');
      }

      // Get tools from store using the same getter as ToolboxSidebar
      const availableToolsFromStore = store.getters['tools/getAvailableTools'];

      // Use the same grouping logic as ToolboxSidebar
      const { groupToolsByMCPServer } = await import(
        'dashboard/helpers/toolHelper'
      );
      const toolsGroupedByServer = groupToolsByMCPServer(
        availableToolsFromStore
      );

      const allToolsFlat = [
        ...toolsGroupedByServer.general,
        ...toolsGroupedByServer.mcpServers.flatMap(server =>
          server.integrations.flatMap(integration => integration.tools)
        ),
      ];

      // Transform tools to match our format, filtering out tools without names
      availableTools = allToolsFlat.map(tool => ({
        id: tool.id || tool.name,
        title: tool.label || tool.title || tool.name,
        label: tool.label || tool.title || tool.name,
        description: tool.description || 'No description available',
        category: tool.category || (tool.type === 'mcp' ? 'MCP' : 'Data'),
        icon: tool.icon || getToolIcon(tool.name || tool.title),
        itemType: 'tool',
        type: tool.type,
        // Include full tool configuration for procedure saving
        arguments: filterInternalRookooArguments(tool.arguments || []),
        mcp: tool.mcp || null,
      }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch tools for suggestions:', error);
      availableTools = [];
    }
  };

  // Get library type icon
  const getLibraryTypeIcon = type => {
    const iconMap = {
      catalog: 'i-lucide-layout-grid',
      gallery: 'i-lucide-book-image',
      documents: 'i-lucide-file', // default type
    };
    return iconMap[type] || 'i-lucide-file'; // fallback to default
  };

  // Fetch libraries (knowledgebases) from store
  const fetchLibraries = async () => {
    try {
      await store.dispatch('knowledgebase/get');
      const allKnowledgebases = store.getters['knowledgebase/getLibraries'];

      // Transform knowledgebases to match our format
      availableLibraries = allKnowledgebases.map(kb => ({
        id: kb.namespace,
        title: kb.name,
        label: kb.name,
        displayTitle: kb.name, // Use just the name without emoji
        description: kb.description || `Knowledge base: ${kb.name}`,
        category: 'Library',
        icon: getLibraryTypeIcon(kb.type),
        itemType: 'library',
        namespace: kb.namespace,
        knowledgebaseId: kb.id,
      }));
    } catch (error) {
      // Handle error silently
      availableLibraries = [];
    }
  };

  // Fetch visualizations (static data from configuration), gated by integrations
  const fetchVisualizations = () => {
    try {
      const getIntegration = store.getters['integrations/getIntegration'];
      availableVisualizations = getAllUi(
        getRestaurantHooksByProvider(getIntegration)
      );
    } catch (error) {
      // Handle error silently
      availableVisualizations = [];
    }
  };

  // Initialize data fetching with a slight delay to allow other components to fetch first
  const initializeData = async () => {
    await store.dispatch('integrations/get');
    await fetchAttributes();
    await fetchLibraries();
    fetchVisualizations();

    // Add a small delay to let the toolbox sidebar fetch first if it's mounting at the same time
    setTimeout(() => {
      fetchTools();
    }, 50);
  };

  // Start initialization
  initializeData();
  return {
    items: ({ query }) => {
      // Combine tools, libraries, attributes, and visualizations for searching
      const allItems = [
        ...availableTools,
        ...availableLibraries,
        ...dynamicAttributes,
        ...availableVisualizations,
      ];

      return allItems
        .filter(item => {
          const searchTerm = query.toLowerCase();
          return (
            (item.displayTitle || item.title)
              .toLowerCase()
              .includes(searchTerm) ||
            item.description.toLowerCase().includes(searchTerm) ||
            item.category.toLowerCase().includes(searchTerm) ||
            item.id.toLowerCase().includes(searchTerm) ||
            item.itemType.toLowerCase().includes(searchTerm)
          );
        })
        .slice(0, 10)
        .map(item => ({
          ...item,
          command: ({ editor, range }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContentAt(range, [
                {
                  type: 'item',
                  attrs: {
                    id: item.id,
                    label: item.label || item.title,
                    icon: item.icon,
                    itemType: item.itemType,
                    attributeModel: item.attributeModel || null,
                    namespace: item.namespace || null,
                  },
                },
                {
                  type: 'text',
                  text: ' ',
                },
              ])
              .run();

            editor.view.dom.ownerDocument.defaultView
              ?.getSelection()
              ?.collapseToEnd();
          },
        }));
    },

    render: () => {
      let component;
      let popup;

      return {
        onStart: props => {
          component = new VueRenderer(ItemSuggestionList, {
            props,
            editor: props.editor,
          });

          if (!props.clientRect) {
            return;
          }

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            theme: 'light-border',
            maxWidth: 400,
          });
        },

        onUpdate(props) {
          component.updateProps(props);

          if (!props.clientRect) {
            return;
          }

          popup[0].setProps({
            getReferenceClientRect: props.clientRect,
          });
        },

        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            popup[0].hide();
            return true;
          }

          return component.ref?.onKeyDown(props);
        },

        onExit() {
          popup[0].destroy();
          component.destroy();
        },
      };
    },

    // Method to refresh attributes when needed
    refreshAttributes: fetchAttributes,
  };
};

// Default export for backward compatibility (without store access)
export default {
  items: ({ query }) => {
    // Note: This default export returns empty tools since we need store access
    // to fetch dynamic data. Use createItemSuggestion(store) instead.
    const tools = [];
    const attributes = [];

    // Combine tools and attributes for searching
    const allItems = [
      ...tools.map(tool => ({ ...tool, itemType: 'tool' })),
      ...attributes.map(attr => ({ ...attr, itemType: 'attribute' })),
    ];
    return allItems
      .filter(item => {
        const searchTerm = query.toLowerCase();
        return (
          item.title.toLowerCase().includes(searchTerm) ||
          item.description.toLowerCase().includes(searchTerm) ||
          item.category.toLowerCase().includes(searchTerm) ||
          item.id.toLowerCase().includes(searchTerm) ||
          item.itemType.toLowerCase().includes(searchTerm)
        );
      })
      .slice(0, 10)
      .map(item => ({
        ...item,
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContentAt(range, [
              {
                type: 'item',
                attrs: {
                  id: item.id,
                  label: item.label || item.title,
                  icon: item.icon,
                  itemType: item.itemType,
                  attributeModel: item.attributeModel || null,
                  namespace: item.namespace || null,
                },
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run();

          editor.view.dom.ownerDocument.defaultView
            ?.getSelection()
            ?.collapseToEnd();
        },
      }));
  },

  render: () => {
    let component;
    let popup;

    return {
      onStart: props => {
        component = new VueRenderer(ItemSuggestionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          theme: 'light-border',
          maxWidth: 400,
        });
      },

      onUpdate(props) {
        component.updateProps(props);

        if (!props.clientRect) {
          return;
        }

        popup[0].setProps({
          getReferenceClientRect: props.clientRect,
        });
      },

      onKeyDown(props) {
        if (props.event.key === 'Escape') {
          popup[0].hide();
          return true;
        }

        return component.ref?.onKeyDown(props);
      },

      onExit() {
        popup[0].destroy();
        component.destroy();
      },
    };
  },
};
